import restClient from "api/restClient";

export type EgressScopeKind = "connector" | "bundle" | "project" | "global";

/** One row of `GET /connector/{id}/egress-policies`. */
export interface ConnectorEgressPolicy {
    id: string;
    name: string | null;
    enabled: boolean;
    /** True when the policy is enabled, i.e. it takes part in the gate's decision. */
    in_effect: boolean;
    scope_kind: EgressScopeKind;
    scope_id: string | null;
    egress_channel: string | null;
    default_action: string;
    rule_count: number;
}

export interface ConnectorEgressPoliciesResult {
    connector_id: string;
    connector_type: string | null;
    egress_channel: string | null;
    /** The workspace EGRESS_DEFAULT_ALLOW setting, used when no policy decides. */
    workspace_default_allow: boolean;
    /** False when the caller may not read egress policies. */
    policies_readable: boolean;
    /** Applicable policies: in effect first, then most specific scope, then name. */
    policies: ConnectorEgressPolicy[];
}

export const connectorEgressPoliciesPath = (connectorId: string) =>
    `/connector/${encodeURIComponent(connectorId)}/egress-policies`;

export const fetchConnectorEgressPolicies = (
    connectorId: string
): Promise<ConnectorEgressPoliciesResult> =>
    restClient
        .get(connectorEgressPoliciesPath(connectorId))
        .then((response) => response?.data as ConnectorEgressPoliciesResult);

const scopeLabels: Record<EgressScopeKind, string> = {
    connector: "This connector",
    bundle: "The connector's bundle",
    project: "The connector's project",
    global: "Whole workspace",
};

/** Where a policy is attached, in words a user reads in the "Applies through" column. */
export const egressScopeLabel = (kind: EgressScopeKind | string | null | undefined): string =>
    scopeLabels[kind as EgressScopeKind] ?? "Whole workspace";

/** What a policy does with a destination that none of its rules match. */
export const egressDefaultActionLabel = (defaultAction: string | null | undefined): string =>
    (defaultAction ?? "open").toLowerCase() === "closed" ? "Block" : "Allow";

/**
 * The explanation above the table. Describes how the gate combines the listed
 * policies, or why nothing is listed.
 */
export const connectorEgressSummary = (result: ConnectorEgressPoliciesResult): string => {
    if (!result.connector_type || !result.egress_channel) {
        return "This connector has no connector type yet, so no egress policy applies to it.";
    }
    const channel = result.egress_channel.toUpperCase();
    const fallback = result.workspace_default_allow ? "allows" : "blocks";
    if (!result.policies_readable) {
        return `You do not have permission to read egress policies, so the policies that govern this connector's ${channel} connections cannot be shown.`;
    }
    const active = result.policies.filter((policy) => policy.in_effect).length;
    if (active === 0) {
        return `No enabled egress policy governs ${channel} connections of this connector. The workspace default ${fallback} them.`;
    }
    return (
        `These egress policies govern ${channel} connections made with this connector. ` +
        "All enabled policies count together: a destination is blocked if any rule blocks it, " +
        "and allowed if a rule allows it. Otherwise it is blocked if any policy blocks unmatched " +
        `destinations by default; if none does, the workspace default ${fallback} it.`
    );
};
